/**
 * Historique des ventes — page du tableau de bord.
 *
 * Elle affichait « Page en cours de développement » alors qu'elle figurait
 * dans le menu (📜 Historique) : une entrée morte depuis le début.
 *
 * Toutes les ventes, de la plus récente à la plus ancienne, et surtout : en
 * dépliant une vente on voit de quel LOT est sortie chaque ligne, de quelle
 * livraison venait ce lot, avec le bon archivé et la photo d'étiquette. C'est
 * la chaîne qu'un contrôle bio remonte — et jusqu'ici elle n'existait que dans
 * la base, sans aucun écran pour la lire.
 *
 * La traçabilité se charge À LA DEMANDE, vente par vente : jointe à la liste,
 * elle ferait quatre jointures par page pour des lignes que personne n'ouvre.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { logger } from '../utils/logger';
import TracabiliteVente from '../components/sales/TracabiliteVente';

const PAGE = 25;

const jourIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const MODES: Record<string, string> = {
  cash: 'Espèces',
  bancontact: 'Bancontact',
  meal_voucher: 'Chèques repas',
  wero: 'Wero',
  wallet: 'FOLIES',
  virement: 'Virement',
  online: 'En ligne',
  mixed: 'Mixte',
};

const euros = (c: number) => (c / 100).toFixed(2).replace('.', ',') + ' €';

export default function SalesHistory() {
  const { tenant } = useTenant();
  const aujourdhui = new Date();
  const ilYaUnMois = new Date();
  ilYaUnMois.setMonth(ilYaUnMois.getMonth() - 1);

  const [du, setDu] = useState(jourIso(ilYaUnMois));
  const [au, setAu] = useState(jourIso(aujourdhui));
  const [recherche, setRecherche] = useState('');
  const [mode, setMode] = useState('');
  const [ventes, setVentes] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [depliee, setDepliee] = useState<string | null>(null);

  const charger = useCallback(async () => {
    if (!tenant) return;
    setChargement(true);
    setErreur(null);
    try {
      const d = await api.getSalesHistory(du, au, {
        search: recherche || undefined,
        paymentMethod: mode || undefined,
        limit: PAGE,
        offset,
      });
      setVentes(d.data ?? d);
      setTotal(d.total ?? (d.data ? d.data.length : d.length));
    } catch (e: any) {
      logger.error('[Historique] chargement échoué:', e);
      setErreur("Impossible de charger l'historique.");
    } finally {
      setChargement(false);
    }
  }, [tenant, du, au, recherche, mode, offset]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Tout changement de filtre ramène à la première page : sinon on reste à la
  // page 4 d'un résultat qui n'en compte plus qu'une, et l'écran semble vide.
  useEffect(() => {
    setOffset(0);
    setDepliee(null);
  }, [du, au, recherche, mode]);

  const pages = Math.max(1, Math.ceil(total / PAGE));
  const page = Math.floor(offset / PAGE) + 1;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Historique des ventes</h1>
        <p className="mt-1 text-gray-600">
          Dépliez une vente pour voir le lot d'origine de chaque article, sa livraison et
          les pièces archivées.
        </p>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Du</span>
            <input
              type="date"
              value={du}
              onChange={(e) => setDu(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Au</span>
            <input
              type="date"
              value={au}
              onChange={(e) => setAu(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Recherche</span>
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="N° de vente, vendeur ou article…"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Paiement</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Tous</option>
              {Object.entries(MODES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {erreur && <p className="text-red-700 font-medium mb-3">{erreur}</p>}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 text-sm text-gray-600">
          {chargement ? 'Chargement…' : `${total} vente${total > 1 ? 's' : ''}`}
        </div>

        {!chargement && ventes.length === 0 && (
          <p className="px-4 py-8 text-center text-gray-500">Aucune vente sur cette période.</p>
        )}

        {ventes.map((v) => {
          const ouverte = depliee === v.id;
          const annulee = v.status === 'cancelled';
          return (
            <div key={v.id} className="border-b border-gray-100 last:border-0">
              <button
                type="button"
                onClick={() => setDepliee(ouverte ? null : v.id)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1"
              >
                <span className="text-gray-500 text-sm tabular-nums w-[118px] shrink-0">
                  {new Date(v.created_at).toLocaleString('fr-BE', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="font-mono text-xs text-gray-700">{v.sale_number}</span>
                {v.vendor_name && <span className="text-sm text-gray-600">{v.vendor_name}</span>}
                <span className="text-sm text-gray-600">
                  {MODES[v.payment_method] || v.payment_method}
                </span>
                {annulee && (
                  <span className="text-xs font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded">
                    annulée
                  </span>
                )}
                <span
                  className={`ml-auto font-bold tabular-nums ${annulee ? 'line-through text-gray-400' : 'text-gray-900'}`}
                >
                  {euros(v.total_ttc_cents ?? v.total_cents ?? 0)}
                </span>
                <span className="text-gray-400 text-sm w-4 text-center">{ouverte ? '▾' : '▸'}</span>
              </button>
              {ouverte && <TracabiliteVente saleId={v.id} />}
            </div>
          );
        })}

        {total > PAGE && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-40"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setOffset(offset + PAGE)}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-40"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
